import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import ProductModal from '../components/ProductModal';
import LoginModal from '../components/LoginModal';
import * as api from '../services/api';
import './Cart.css';

export default function Cart() {
  const navigate = useNavigate();
  const { cart, loading, updateCartItem, removeFromCart, clearCart, loadCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [shouldNavigateToCheckout, setShouldNavigateToCheckout] = useState(false);

  useEffect(() => {
    loadCart();
    loadCoupons();
  }, []);

  // Navigate to checkout after successful login
  useEffect(() => {
    if (shouldNavigateToCheckout && isAuthenticated) {
      setShouldNavigateToCheckout(false);
      navigate('/checkout');
    }
  }, [isAuthenticated, shouldNavigateToCheckout, navigate]);

  const loadCoupons = async () => {
    try {
      const activeCoupons = await api.getActiveCoupons();
      setCoupons(activeCoupons);
    } catch (error) {
      console.error('Error loading coupons:', error);
    }
  };

  const handleProductClick = async (productId) => {
    try {
      const product = await api.getProduct(productId);
      setSelectedProduct(product);
      setShowProductModal(true);
    } catch (error) {
      console.error('Error loading product:', error);
      alert('Failed to load product details');
    }
  };

  const handleCouponSelect = async (coupon) => {
    if (selectedCoupon?.code === coupon.code) {
      // Deselect if already selected
      setSelectedCoupon(null);
      setCouponDiscount(0);
      setCouponCode('');
      return;
    }

    try {
      const itemsTotal = calculateItemsTotal();
      const result = await api.validateCoupon(coupon.code, itemsTotal);
      if (result.valid) {
        setSelectedCoupon(coupon);
        setCouponDiscount(result.discount);
        setCouponCode(coupon.code);
        setCouponError('');
      } else {
        setCouponError(result.message || 'Invalid coupon');
      }
    } catch (error) {
      setCouponError(error.response?.data?.message || 'Failed to apply coupon');
    }
  };

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(itemId);
    } else {
      await updateCartItem(itemId, newQuantity);
    }
    // Re-validate coupon if applied
    if (selectedCoupon) {
      const itemsTotal = calculateItemsTotal();
      try {
        const result = await api.validateCoupon(selectedCoupon.code, itemsTotal);
        if (result.valid) {
          setCouponDiscount(result.discount);
        } else {
          setSelectedCoupon(null);
          setCouponDiscount(0);
          setCouponCode('');
        }
      } catch (error) {
        console.error('Error re-validating coupon:', error);
      }
    }
  };

  const handleRemove = async (itemId) => {
    if (window.confirm('Remove this item from cart?')) {
      await removeFromCart(itemId);
      // Re-validate coupon if applied
      if (selectedCoupon) {
        const itemsTotal = calculateItemsTotal();
        try {
          const result = await api.validateCoupon(selectedCoupon.code, itemsTotal);
          if (result.valid) {
            setCouponDiscount(result.discount);
          } else {
            setSelectedCoupon(null);
            setCouponDiscount(0);
            setCouponCode('');
          }
        } catch (error) {
          console.error('Error re-validating coupon:', error);
        }
      }
    }
  };

  const handleClearCart = async () => {
    if (window.confirm('Clear all items from cart?')) {
      await clearCart();
      setSelectedCoupon(null);
      setCouponDiscount(0);
      setCouponCode('');
    }
  };

  const calculateItemsTotal = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const price = item.variant?.price || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const calculateItemsMRP = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const product = item.product || {};
      const price = item.variant?.price || product.price || 0;
      // Use variant MRP if available, otherwise use product MRP, fallback to price
      const variantMrp = item.variant?.mrp;
      const productMrp = product.mrp;
      const mrp = variantMrp && variantMrp > price 
        ? variantMrp 
        : (productMrp && productMrp > price ? productMrp : price);
      return total + (mrp * item.quantity);
    }, 0);
  };

  const calculateItemTotal = (item) => {
    const price = item.variant?.price || item.product?.price || 0;
    return price * item.quantity;
  };

  const calculateItemMRP = (item) => {
    const product = item.product || {};
    const price = item.variant?.price || product.price || 0;
    // Use variant MRP if available, otherwise use product MRP, fallback to price
    const variantMrp = item.variant?.mrp;
    const productMrp = product.mrp;
    const mrp = variantMrp && variantMrp > price 
      ? variantMrp 
      : (productMrp && productMrp > price ? productMrp : price);
    return mrp * item.quantity;
  };

  const itemsTotal = calculateItemsTotal();
  const itemsMRP = calculateItemsMRP();
  const productDiscount = Math.max(0, itemsMRP - itemsTotal); // Ensure non-negative
  const handlingCharge = 5; // Fixed handling charge
  const deliveryCharge = itemsTotal < 100 ? 20 : 0; // ₹20 if cart value < ₹100, else free
  const grandTotal = itemsTotal - couponDiscount + handlingCharge + deliveryCharge;
  // Total savings = product discount + coupon discount
  const totalSavings = productDiscount + couponDiscount;

  if (loading) {
    return (
      <div className="cart-container">
        <div className="loading-state">
          <p>Loading cart...</p>
        </div>
      </div>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="cart-container">
        <div className="cart-header">
          <button onClick={() => navigate('/')} className="back-button">
            ← Continue Shopping
          </button>
          <h1>Your Cart</h1>
        </div>
        <div className="empty-cart">
          <div className="empty-cart-icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Add some products to your cart to get started!</p>
          <button onClick={() => navigate('/')} className="btn primary">
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Continue Shopping
        </button>
        <h1>Your Cart ({cart.items.length} {cart.items.length === 1 ? 'item' : 'items'})</h1>
        {cart.items.length > 0 && (
          <button onClick={handleClearCart} className="clear-cart-btn">
            Clear Cart
          </button>
        )}
      </div>

      <div className="cart-content">
        <div className="cart-items">
          {cart.items.map((item) => {
            const product = item.product || {};
            const variant = item.variant || {};
            const name = product.name || product.title || 'Product';
            const price = variant.price || product.price || 0;
            // Use variant MRP if available, otherwise use product MRP, fallback to price
            const variantMrp = variant.mrp;
            const productMrp = product.mrp;
            const mrp = variantMrp && variantMrp > price 
              ? variantMrp 
              : (productMrp && productMrp > price ? productMrp : price);
            const label = variant.sizeLabel || variant.label || '';
            const itemTotal = calculateItemTotal(item);
            const itemMRP = calculateItemMRP(item);

            return (
              <div key={item._id || item.productId} className="cart-item">
                <div className="cart-item-details">
                  <h3 
                    className="cart-item-name"
                    onClick={() => handleProductClick(item.product?._id || item.productId)}
                  >
                    {name}
                  </h3>
                  {label && <p className="cart-item-variant">{label}</p>}
                  <div className="cart-item-price-row">
                    <span className="cart-item-price">₹{price}</span>
                    {mrp > price && (
                      <span className="cart-item-mrp">₹{mrp}</span>
                    )}
                  </div>
                </div>
                <div className="cart-item-actions">
                  <div className="quantity-controls">
                    <button 
                      onClick={() => handleQuantityChange(item._id || item.productId, item.quantity - 1)}
                      className="qty-btn"
                    >
                      −
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      onClick={() => handleQuantityChange(item._id || item.productId, item.quantity + 1)}
                      className="qty-btn"
                    >
                      +
                    </button>
                  </div>
                  <div className="cart-item-total">
                    ₹{itemTotal}
                    {itemMRP > itemTotal && (
                      <span className="cart-item-savings">Saved ₹{itemMRP - itemTotal}</span>
                    )}
                  </div>
                  <button 
                    onClick={() => handleRemove(item._id || item.productId)}
                    className="remove-btn"
                    title="Remove item"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="cart-summary">
          {/* Coupon Section */}
          <div className="coupon-section">
            <h3>Apply Coupon</h3>
            {coupons.length > 0 ? (
              <div className="coupon-dropdown">
                <select
                  value={couponCode}
                  onChange={(e) => {
                    const code = e.target.value;
                    if (code) {
                      const coupon = coupons.find(c => c.code === code);
                      if (coupon) handleCouponSelect(coupon);
                    } else {
                      setSelectedCoupon(null);
                      setCouponDiscount(0);
                      setCouponCode('');
                      setCouponError('');
                    }
                  }}
                  className="coupon-select"
                >
                  <option value="">Select a coupon</option>
                  {coupons.map((coupon) => (
                    <option key={coupon._id} value={coupon.code}>
                      {coupon.code} - {coupon.description || `${coupon.discountValue}${coupon.discountType === 'percentage' ? '%' : '₹'} off`}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="no-coupons">No active coupons available</p>
            )}
            {couponError && <p className="coupon-error">{couponError}</p>}
            {selectedCoupon && (
              <div className="applied-coupon">
                ✓ Applied: {selectedCoupon.code} - Save ₹{couponDiscount}
              </div>
            )}
          </div>

          {/* Bill Details */}
          <div className="summary-card">
            <h2>Bill details</h2>
            
            <div className="bill-row">
              <div className="bill-label">
                <span className="bill-icon">📄</span>
                <span>Items total</span>
                {productDiscount > 0 && (
                  <span className="savings-badge">Saved ₹{productDiscount}</span>
                )}
              </div>
              <div className="bill-value">
                {itemsMRP > itemsTotal && (
                  <span className="strikethrough">₹{itemsMRP}</span>
                )}
                <span>₹{itemsTotal}</span>
              </div>
            </div>

            {couponDiscount > 0 && (
              <div className="bill-row">
                <div className="bill-label">
                  <span className="bill-icon">🎟️</span>
                  <span>Coupon discount</span>
                </div>
                <div className="bill-value discount">
                  -₹{couponDiscount}
                </div>
              </div>
            )}

            <div className="bill-row">
              <div className="bill-label">
                <span className="bill-icon">🛵</span>
                <span>Delivery charge</span>
                {deliveryCharge === 0 && itemsTotal >= 100 && (
                  <span className="info-icon" title="Free delivery on orders above ₹100">ℹ️</span>
                )}
              </div>
              <div className="bill-value">
                {deliveryCharge === 0 ? (
                  <>
                    <span className="strikethrough">₹20</span>
                    <span className="free-text">FREE</span>
                  </>
                ) : (
                  <span>₹{deliveryCharge}</span>
                )}
              </div>
            </div>

            <div className="bill-row">
              <div className="bill-label">
                <span className="bill-icon">🛍️</span>
                <span>Handling charge</span>
                <span className="info-icon" title="Packaging and handling fee">ℹ️</span>
              </div>
              <div className="bill-value">₹{handlingCharge}</div>
            </div>

            <div className="bill-row grand-total">
              <div className="bill-label">
                <strong>Grand total</strong>
              </div>
              <div className="bill-value">
                <strong>₹{grandTotal}</strong>
              </div>
            </div>

            <div className="total-savings">
              <div className="savings-label">Your total savings</div>
              <div className="savings-value">₹{totalSavings}</div>
            </div>

            <button 
              className="btn primary checkout-btn"
              onClick={() => {
                if (!isAuthenticated) {
                  setShouldNavigateToCheckout(true);
                  setShowLoginModal(true);
                } else {
                  navigate('/checkout');
                }
              }}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>

      {showProductModal && selectedProduct && (
        <ProductModal
          product={selectedProduct}
          isOpen={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {showLoginModal && (
        <LoginModal 
          onClose={() => {
            setShowLoginModal(false);
            // Navigation to checkout will be handled by useEffect when isAuthenticated becomes true
          }}
        />
      )}
    </div>
  );
}
